import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Sparkles, X as XIcon, Check, Gift } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { trackLead } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Routes where the popup MUST NOT show (auth flows, payment, admin, unsubscribe).
const BLOCKED_PATH_PREFIXES = [
    "/connexion", "/inscription",
    "/payment/", "/admin",
    "/newsletter/desinscription",
    "/cgv", "/mentions-legales", "/confidentialite",
];

const LS_DISMISSED = "fol_nl_dismissed_at";
const LS_SUBSCRIBED = "fol_nl_subscribed";

// Show again after 30 days if user dismissed
const REDISPLAY_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

// Trigger timings (whichever first)
const TIME_ON_SITE_MS = 45_000;      // 45 seconds
const SCROLL_THRESHOLD = 0.6;         // 60% of page scrolled

export default function NewsletterPopup() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(false);
    const [gift, setGift] = useState(null);
    const armedRef = useRef(false);

    useEffect(() => {
        http.get("/newsletter/welcome-gift").then((r) => setGift(r.data)).catch(() => {});
    }, []);

    const shouldSkip = useCallback(() => {
        // Route-based exclusions
        if (BLOCKED_PATH_PREFIXES.some((p) => pathname.startsWith(p))) return true;
        // Logged-in members already subscribed to the newsletter — works even in
        // private/incognito windows because the flag comes from /api/auth/me,
        // not from localStorage.
        if (user?.newsletter_subscribed === true) return true;
        // Already dismissed recently
        try {
            const dismissedAt = localStorage.getItem(LS_DISMISSED);
            if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < REDISPLAY_AFTER_MS) return true;
            if (localStorage.getItem(LS_SUBSCRIBED) === "1") return true;
        } catch {}
        return false;
    }, [pathname, user]);

    const arm = useCallback(() => {
        if (armedRef.current) return;
        if (shouldSkip()) return;
        armedRef.current = true;
        setOpen(true);
    }, [shouldSkip]);

    useEffect(() => {
        if (shouldSkip()) return;

        // 1) Time-based trigger
        const timeTimer = setTimeout(arm, TIME_ON_SITE_MS);

        // 2) Scroll-based trigger
        const onScroll = () => {
            const scrolled = window.scrollY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
            if (scrolled >= SCROLL_THRESHOLD) arm();
        };
        window.addEventListener("scroll", onScroll, { passive: true });

        // 3) Exit-intent trigger (desktop only)
        const onMouseOut = (e) => {
            if (e.clientY <= 5 && !e.relatedTarget && !e.toElement) arm();
        };
        if (window.matchMedia("(min-width: 768px)").matches) {
            document.addEventListener("mouseout", onMouseOut);
        }

        return () => {
            clearTimeout(timeTimer);
            window.removeEventListener("scroll", onScroll);
            document.removeEventListener("mouseout", onMouseOut);
        };
    }, [arm, shouldSkip]);

    // Pre-fill email/name if user logged in (rare — they usually already subscribed)
    useEffect(() => {
        if (user?.email && !email) setEmail(user.email);
        // Fall back to `first_name` (new accounts) or the first word of `name`
        // (legacy accounts) so the greeting stays personal.
        const firstFromUser = user?.first_name || (user?.name ? String(user.name).split(" ")[0] : "");
        if (firstFromUser && !firstName) setFirstName(firstFromUser);
    }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

    // If the logged-in user is already subscribed, close any open popup and
    // mirror the state in localStorage so we stay quiet on the next visits too.
    useEffect(() => {
        if (user?.newsletter_subscribed === true) {
            try { localStorage.setItem(LS_SUBSCRIBED, "1"); } catch {}
            setOpen(false);
        }
    }, [user?.newsletter_subscribed]);

    const dismiss = () => {
        try { localStorage.setItem(LS_DISMISSED, String(Date.now())); } catch {}
        setOpen(false);
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        const em = (email || "").trim().toLowerCase();
        if (!em || !em.includes("@")) return toast.error("Merci d'indiquer une adresse email valide.");
        setBusy(true);
        try {
            await http.post("/newsletter/subscribe", {
                email: em,
                name: (firstName || "").trim() || undefined,
                source: "popup",
            });
            try { localStorage.setItem(LS_SUBSCRIBED, "1"); } catch {}
            trackLead("newsletter_popup");
            setSuccess(true);
            setTimeout(() => setOpen(false), 3000);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
            data-testid="newsletter-popup"
            onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
        >
            <div className="relative w-full max-w-md rounded-3xl bg-card border border-primary/20 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 duration-500">
                <button
                    type="button"
                    onClick={dismiss}
                    className="absolute top-3 right-3 p-2 rounded-full hover:bg-muted/60 transition-colors z-10"
                    data-testid="newsletter-popup-close"
                    aria-label="Fermer"
                >
                    <XIcon className="w-4 h-4 text-muted-foreground" />
                </button>

                {success ? (
                    <div className="px-8 py-12 text-center">
                        <div className="mx-auto w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mb-4">
                            <Check className="w-7 h-7 text-primary" />
                        </div>
                        <h3 className="font-serif text-2xl mb-2">Bienvenue dans la lumière ✨</h3>
                        <p className="text-sm text-foreground/70 leading-relaxed">
                            Vérifiez votre boîte de réception — un premier message vient de partir. Pensez à ajouter <strong className="font-mono">reservation@foundation-of-light.com</strong> à votre carnet d'adresses pour le recevoir en boîte principale.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="bg-gradient-to-br from-primary/10 via-secondary/10 to-transparent px-8 pt-10 pb-6 text-center">
                            <div className="mx-auto w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mb-4">
                                <Sparkles className="w-7 h-7 text-primary" strokeWidth={1.8} />
                            </div>
                            <h3 className="font-serif text-2xl mb-2">Tissons la lumière ensemble</h3>
                            <p className="text-sm text-foreground/70 leading-relaxed">
                                Recevez chaque mois nos ateliers à venir, méditations guidées et messages inspirants — directement dans votre boîte mail, sans jamais de spam.
                            </p>
                            {gift?.is_active && (
                                <div className="mt-5 rounded-2xl border-2 border-[#c9a66b]/50 bg-[#c9a66b]/10 overflow-hidden text-left flex items-stretch gap-0" data-testid="newsletter-popup-gift-banner">
                                    {gift.cover_url ? (
                                        <img
                                            src={gift.cover_url}
                                            alt={gift.title || "Cadeau de bienvenue"}
                                            className="w-24 h-24 sm:w-28 sm:h-28 object-cover shrink-0"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-24 h-24 sm:w-28 sm:h-28 bg-[#c9a66b]/20 flex items-center justify-center shrink-0">
                                            <Gift className="w-8 h-8 text-[#c9a66b]" strokeWidth={1.5} />
                                        </div>
                                    )}
                                    <div className="p-3 sm:p-4 flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-widest text-[#c9a66b] font-semibold mb-1 flex items-center gap-1">
                                            <Gift className="w-3 h-3" /> Cadeau offert
                                        </div>
                                        {gift.title && (
                                            <p className="font-serif text-sm sm:text-base text-foreground leading-snug mb-0.5 line-clamp-2">
                                                {gift.title}
                                            </p>
                                        )}
                                        {gift.duration_minutes && (
                                            <p className="text-[11px] text-foreground/60">
                                                Durée : {gift.duration_minutes} min
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <form onSubmit={submit} className="px-8 pb-8 pt-4 space-y-3">
                            <Input
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="Votre prénom (facultatif)"
                                className="rounded-full h-11"
                                data-testid="newsletter-popup-name"
                                autoComplete="given-name"
                            />
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="votre@email.com"
                                className="rounded-full h-11"
                                data-testid="newsletter-popup-email"
                                autoComplete="email"
                                required
                                autoFocus
                            />
                            <Button
                                type="submit"
                                disabled={busy}
                                className="w-full rounded-full h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                                data-testid="newsletter-popup-submit"
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                {busy ? "Inscription en cours…" : "Je m'inscris"}
                            </Button>
                            <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
                                En vous inscrivant, vous acceptez notre <a href="/confidentialite" className="underline">politique de confidentialité</a>. Désinscription en un clic dans chaque email.
                            </p>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
